import { derived, get } from "svelte/store";

import { avail_devices, selected_devices, device_permissions, has_joined, setSelectedDevice } from "./stores.js";
import { Devices } from "./Devices.js";
import { OutputConfig, BuildSettings } from "./OutputConfig.js";
import { UserPrefs } from "./UserPrefs.js";
import { Logger } from "./Logger.js";

/*
 "Best audio": while a Bluetooth headset's mic is open the OS holds it in its call profile (HFP),
 so everything heard through it -- the stream included -- is low bandwidth. Each platform
 strategy decides whether to offer a fix and how to apply it; the toolbar button and modal
 render whatever offer the active strategy returns.
*/

// -- Chrome's alias entries: never a concrete target
const PSEUDO_IDS = new Set(["default", "communications"]);

// -- Chrome appends "(Bluetooth)" itself on mac + win, never localized; AirPods lose it before M150
const BLUETOOTH_RE = /\(Bluetooth(?: LE)?\)|\bAirPods\b|Hands-Free/i;

// -- loopback / virtual inputs capture nothing useful as a conference mic (mac labels them "(Virtual)")
const VIRTUAL_RE = /\((?:Virtual|Aggregate|AutoAggregate)\)\s*$|Virtual (?:Audio|Cable|Desktop|Soundcard|Mic|Input|Device)|BlackHole|Loopback|Soundflower|Zoom ?Audio|Teams Audio|Krisp|Audio Bridge|Pro Tools Aggregate|Dante (?:Virtual Soundcard|Via)|Audio Hijack|Background Music|\bOBS\b|VB-Audio|CABLE Output|Voicemeeter|Stereo Mix|What U Hear|Wave Out Mix|NVIDIA Broadcast|Steam Streaming|Parsec|iShowU|\bCamo\b|mmhmm|Ecamm|Descript|Snap Camera|Nahimic mirroring|SteelSeries Sonar|Wave Link|Voicemod|Voice\.ai/i;

const USB_SUFFIX_RE = /\s\([0-9a-f]{4}:[0-9a-f]{4}\)\s*$/i;

const STRATEGIES = {

    // -- releasing the Bluetooth mic is enough: the OS returns the headset to A2DP by itself
    chrome_desktop: {
        offer: (track, mics, opts) => BestAudio.micSwitchOffer(track, mics, opts),
        apply: (offer) => BestAudio.switchMic(offer),
    },
};

export const BestAudio = {

    _offered: new Set(),
    _silent: new Set(),     // -- deviceIds that opened but delivered no signal this page

    // -- every desktop Chromium reports as "Chrome" (Edge, Brave, Arc...), fine since the fix is OS-level
    platform(browser = OutputConfig.session?.browser){
        if (browser?.engine === "chromium" && !browser.isMobile)
            return "chrome_desktop";
        return null;
    },

    // -- the one gate: no strategy means no offer, no button and no apply
    strategy(){
        if (BuildSettings.DISABLE_BEST_AUDIO) return null;
        return STRATEGIES[this.platform()] ?? null;
    },

    isBluetoothMic(label){
        return BLUETOOTH_RE.test(label ?? "");
    },

    isVirtualMic(label){
        return VIRTUAL_RE.test(label ?? "");
    },

    displayLabel(label){
        return (label ?? "").replace(USB_SUFFIX_RE, "");
    },

    // -- lower is better: built-in, then internal mic arrays (win), then USB; line/digital inputs last
    _rank(label){
        if (/Digital Input|S\/?PDIF|\bLine[ -]?In\b/i.test(label)) return 5;
        if (/\(Built-in\)\s*$|^Built-in mic$/i.test(label)) return 0;
        if (/\b(?:Microphone|Mic)\b/i.test(label) && /Array|Internal|Integrated|Realtek|Smart Sound/i.test(label)) return 1;
        if (USB_SUFFIX_RE.test(label)) return 2;
        if (/iPhone|iPad/i.test(label)) return 4;
        return 3;
    },

    // -- pure: the live mic track + device lists -> an offer or null. `sink` is the output the stream
    // -- plays on (MediaDeviceInfo, when known); `exclude` holds deviceIds never to offer.
    micSwitchOffer(track, mics = [], { sink = null, exclude = new Set() } = {}){

        if (!track || track.readyState !== "live")
            return null;

        const settings = track.getSettings?.() ?? {};

        // -- an alias entry follows the OS default after open; the track label is fixed at open
        const current = PSEUDO_IDS.has(settings.deviceId)
            ? mics.find(d => !PSEUDO_IDS.has(d.deviceId) && (d.label === track.label || track.label.endsWith(" - " + d.label))) ?? null
            : mics.find(d => d.deviceId === settings.deviceId) ?? mics.find(d => d.label === track.label) ?? null;
        const current_label = current?.label || track.label;

        if (!this.isBluetoothMic(current_label))
            return null;

        const group = current?.groupId || settings.groupId || null;
        const target = mics
            .filter(d => d.label
                && !PSEUDO_IDS.has(d.deviceId)
                && !exclude.has(d.deviceId)
                && d.deviceId !== current?.deviceId
                && !(group && d.groupId === group)
                && !this.isBluetoothMic(d.label)
                && !this.isVirtualMic(d.label))
            .sort((a, b) => this._rank(a.label) - this._rank(b.label))[0];

        if (!target)
            return null;

        return {
            kind: "switch-mic",
            from_label: this.displayLabel(current_label),
            to_label: this.displayLabel(target.label),
            target: target,
            // -- null when unknown; false means the stream plays elsewhere and only the mic is affected
            on_headset: sink ? (!!group && sink.groupId === group) || this.isBluetoothMic(sink.label) : null,
            sink_label: sink?.label ?? null,
            // -- the mic's device rate, for the logs: HFP inputs run at 16-24 kHz (mac)
            rate_hint: track.getCapabilities?.().sampleRate?.min ?? null,
        };
    },

    // -- the output the stream plays on: the stream selection (clients mirror the speaker into it)
    _sink(selected, speakers){
        const sel = (selected.stream?.id || selected.stream?.label) ? selected.stream : selected.speaker;
        const id = sel?.id || "default";
        return speakers.find(d => d.deviceId === id) ?? speakers.find(d => d.label === sel?.label) ?? null;
    },

    // -- the modal's OK
    apply(offer){
        const strategy = this.strategy();
        if (!strategy || !offer)
            return Promise.resolve({ ok: false, reason: "unsupported" });
        return strategy.apply(offer);
    },

    async switchMic(offer){

        const log_meta = { from: offer.from_label, to: offer.to_label, sink: offer.sink_label };
        const fail = (reason) => {
            Logger.log("Devices", "warn", "Best Audio Switch Failed", { ...log_meta, reason });
            return { ok: false, reason };
        };

        // -- the list can change while the modal is open
        const target = (get(avail_devices).microphone ?? []).find(d => d.deviceId === offer.target?.deviceId);
        if (!target)
            return fail("target-gone");

        const previous = Devices.tracks.audio.track;

        let error = null;
        try {
            await Devices.selectInputDevice("microphone", target.label);
        }
        catch (e){
            error = e;
        }

        // -- a busy target resolves anyway and snaps back to the old mic: judge by what actually opened
        const open = Devices.tracks.audio;
        const ok = open.id === target.deviceId && open.track?.readyState === "live";

        if (!ok){
            // -- a rejected pass skips Devices' snap-back and leaves the selection naming the target
            if (error && open.track)
                setSelectedDevice("microphone", { label: open.track.label, id: open.id });
            return fail(error?.name ?? "not-opened");
        }

        // -- a listed-but-dead input (a MacBook mic with the lid closed) opens live and delivers zeros
        if (!(await this._hasSignal(open.track))){
            this._silent.add(target.deviceId);
            if (previous?.label)
                await Devices.selectInputDevice("microphone", previous.label).catch(e => console.error(e));
            return fail("silent");
        }

        // -- prefs are otherwise only written by the Join click; without this a reload reopens the BT mic
        if (get(has_joined))
            UserPrefs.writeMicrophone(target.label).catch(e => console.error(e));

        Logger.log("Devices", "info", "Best Audio Switched", { ...log_meta, error: error?.name ?? null });
        return { ok: true };
    },

    // -- exact zeros for the whole window = no input at all; any failure to probe counts as signal
    async _hasSignal(track, ms = 1500){

        let probe = null;
        let ctx = null;

        try {
            probe = track.clone();
            probe.enabled = true;   // -- a muted mic's base track is disabled after Join
            ctx = new AudioContext({ sinkId: { type: "none" } });
            const analyser = ctx.createAnalyser();
            ctx.createMediaStreamSource(new MediaStream([probe])).connect(analyser);
            await ctx.resume();

            const buf = new Float32Array(analyser.fftSize);
            for (let waited = 0; waited < ms; waited += 100){
                await new Promise(r => setTimeout(r, 100));
                analyser.getFloatTimeDomainData(buf);
                if (buf.some(v => v !== 0))
                    return true;
            }
            return ctx.state !== "running";
        }
        catch (e){
            console.warn("[PL-BestAudio] signal probe failed", e);
            return true;
        }
        finally {
            probe?.stop();
            ctx?.close().catch(() => {});
        }
    },

    logOffered(offer){
        const key = `${offer.from_label}>${offer.to_label}`;
        if (this._offered.has(key))
            return;
        this._offered.add(key);
        Logger.log("Devices", "info", "Best Audio Offered", {
            platform: this.platform(), from: offer.from_label, to: offer.to_label,
            sink: offer.sink_label, on_headset: offer.on_headset, rate_hint: offer.rate_hint,
        });
    },

    logDeclined(offer){
        Logger.log("Devices", "info", "Best Audio Declined", { from: offer?.from_label, to: offer?.to_label });
    },
};

// -- null unless the active platform strategy has something to offer right now
export const best_audio_offer = derived(
    [avail_devices, selected_devices, device_permissions],
    ([$avail, $selected, $permissions]) => {
        const strategy = BestAudio.strategy();
        if (!strategy || OutputConfig.session?.disable_conf || $permissions.microphone !== "granted")
            return null;
        return strategy.offer(get($selected.microphone.track), $avail.microphone ?? [], {
            sink: BestAudio._sink($selected, $avail.speaker ?? []),
            exclude: BestAudio._silent,
        });
    }
);
